// Sales invoices and sales returns of every branch, for the consolidated reports.
//
// The consolidated endpoints (Reports/SalesReportConsol, ItemWiseConsolReport)
// read the legacy POS sales tables only. Every sale raised in this app - the
// Sales Invoice screen and the POS billing screen - is an InvSale document
// (TransType 2, returns TransType 3) and never reaches them, so on its own a
// consolidated report shows nothing for any recent period. Until the backend
// consolidates InvSale (docs/BACKEND-COMPLETE.md), each consolidated page adds
// these rows to what the consolidated endpoint returns. Both sources are real
// records from separate tables; nothing is double counted.
import dayjs from "dayjs";
import { getBranchList } from "../../../services/CoreApiServices";
import { getSalesByTransType } from "../../../services/SalesApiServices";

const num = (v) => Number(v) || 0;

export const SALES_INVOICE = 2;
export const SALES_RETURN = 3;

// salesPaymentType on an InvSale document
export const INVOICE_PAYMENT = { 0: "Cash", 1: "Credit", 2: "UPI", 3: "Card" };
export const INVOICE_CREDIT = 1;

// The branches the signed-in user works in (state.userBranch.BranchByUser),
// else every branch of the company.
export async function consolidatedBranches(branchByUser) {
  const mine = (Array.isArray(branchByUser) ? branchByUser : [])
    .map((o) => o?.branch || o)
    .filter((b) => b && b.guid);
  if (mine.length) return mine;
  const all = await getBranchList();
  return (Array.isArray(all) ? all : []).filter((b) => b && b.guid && !b.isDeleted);
}

const normalise = (inv, branchName, isReturn) => ({
  guid: inv?.guid,
  branch: branchName,
  date: inv?.invdate ? dayjs(inv.invdate).format("YYYY-MM-DD") : "",
  isReturn,
  paymentType: inv?.salesPaymentType,
  payment: INVOICE_PAYMENT[inv?.salesPaymentType] || "Other",
  gross: num(inv?.grossTotal),
  extra: num(inv?.additionalAmt),
  discount: num(inv?.discountTotal),
  tax: num(inv?.taxTotal),
  roundOff: num(inv?.roundOff),
  net: num(inv?.netTotal),
  cash: num(inv?.cash),
  card: num(inv?.card),
  bank: num(inv?.bank),
  upi: num(inv?.upi),
  lines: (inv?.invSalesDetails || []).map((l) => ({
    product: l?.product?.name || "Unknown item",
    quantity: num(l?.quantity),
    // before tax, after the line discount - the same basis as the POS
    // item-wise consolidated figure (total - discount)
    value: num(l?.amount) - num(l?.discAmt),
  })),
});

// One invoice as a row of the PayMode / PayType branch summary, on the same
// basis the pages use for POS rows: gross = total + extra - discount (before
// tax), net = gross - cancelled - complementary - refund. A return is a refund
// of its net total. Card takes every non-cash tender (card, bank, UPI).
export const invoicePaySummaryRow = (r, dateFormat = "DD MMM YYYY") => {
  const trnDate = dayjs(r.date).format(dateFormat);
  const zero = {
    branch: r.branch,
    trnDate,
    complementary: 0,
    cancelled: 0,
    refund: 0,
    gross: 0,
    discount: 0,
    netTotal: 0,
    total: 0,
    cash: 0,
    card: 0,
    credit: 0,
    extra: 0,
  };
  if (r.isReturn) return { ...zero, refund: r.net, netTotal: -r.net };
  const gross = r.gross + r.extra - r.discount;
  return {
    ...zero,
    gross,
    discount: r.discount,
    netTotal: gross,
    total: r.gross,
    extra: r.extra,
    cash: r.cash,
    card: r.card + r.bank + r.upi,
    credit: r.paymentType === INVOICE_CREDIT ? r.net : 0,
  };
};

// Invoices and returns of `branches` dated from..to (YYYY-MM-DD, inclusive).
// A branch whose call fails is skipped, not the whole report.
export async function fetchConsolidatedInvoices(branches, from, to) {
  const jobs = (branches || []).flatMap((b) =>
    [SALES_INVOICE, SALES_RETURN].map((type) =>
      getSalesByTransType(b, type, from, to).then((res) =>
        (Array.isArray(res?.data) ? res.data : []).map((inv) =>
          normalise(inv, b.name || "Branch", type === SALES_RETURN)
        )
      )
    )
  );
  const settled = await Promise.allSettled(jobs);
  return settled
    .flatMap((s) => (s.status === "fulfilled" ? s.value : []))
    .filter((r) => r.date && r.date >= from && r.date <= to);
}
